/**
 * Utility functions for image URL transformations
 * Used by PropertyFeatureImage and PropertyImagePreview components
 */

export type ImageSize = "thumbnail" | "standard" | "fullsize" | "web";

/**
 * Transform image URL based on size and origin for optimal display
 *
 * @param imageUrl - Base image URL
 * @param origin - Image origin (e.g., "SiteIQ")
 * @param size - Desired image size
 * @returns Transformed URL with appropriate size parameters
 */
export const transformImageUrl = (
  imageUrl: string,
  origin: string,
  size: ImageSize,
): string => {
  if (!imageUrl) return "";

  // Handle SiteIQ images with special transformation logic
  if (origin?.toLowerCase() === "siteiq") {
    if (!imageUrl.includes("edp?") && !imageUrl.includes("s3.amazonaws.com")) {
      const fileNameString = imageUrl;
      const lastFiveChar = fileNameString.substring(fileNameString.length - 5);
      const fileExtensionFlag = lastFiveChar.includes(".");

      if (!fileExtensionFlag) {
        // No file extension - append size suffix
        if (size === "thumbnail") {
          return `${imageUrl}_thumb`;
        } else if (size === "standard") {
          return `${imageUrl}_standard`;
        }
        return imageUrl; // fullsize returns original
      } else {
        // Has file extension - use query parameter
        return `${imageUrl}?d=${size}`;
      }
    }
    return imageUrl; // Already processed or S3 URL
  }

  // For non-SiteIQ images, apply query parameters
  if (size === "thumbnail") {
    return imageUrl.includes("?")
      ? `${imageUrl}&d=thumbnail`
      : `${imageUrl}?d=thumbnail`;
  } else if (size === "web" || size === "standard") {
    return imageUrl.includes("?")
      ? `${imageUrl}&d=standard`
      : `${imageUrl}?d=standard`;
  }

  return imageUrl; // fullsize returns original
};

/**
 * Get responsive image classes based on size and orientation
 *
 * @param size - Image size type
 * @param isPortrait - Whether the layout is portrait orientation
 * @returns CSS class string for image dimensions
 */
export const getImageClasses = (
  size: ImageSize,
  isPortrait?: boolean,
): string => {
  if (size === "fullsize") {
    return "w-full h-full";
  } else if (size === "standard") {
    if (isPortrait) {
      return "w-full aspect-16/10"; // Portrait dimensions
    } else {
      return "w-full aspect-16/10"; // Landscape dimensions
    }
  }

  return "w-full aspect-16/10"; // thumbnail default
};
